import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '@/hooks/useWebSocket';
import {
  Box, Badge, IconButton, Popover, Typography, List, ListItem,
  ListItemIcon, ListItemText, Stack, Button, Divider, Chip,
  Tooltip, alpha, Skeleton,
} from '@mui/material';
import {
  Notifications as NotifIcon,
  CheckCircleOutline as ReadIcon,
  DoneAll as MarkAllIcon,
  Circle as UnreadDot,
  AttachMoney as PayrollIcon,
  EventAvailable as LeaveIcon,
  Home as RentIcon,
  SwapHoriz as IntercoIcon,
  Info as DefaultIcon,
  Message as MessageIcon,
  OpenInNew as ViewAllIcon,
  NotificationsNone as EmptyIcon,
  Celebration as WelcomeIcon,
  Settings as SetupIcon,
  CalendarMonth as HolidayIcon,
  Lightbulb as TipIcon,
} from '@mui/icons-material';
import { notificationsApi, NotificationResponse } from '@/api/endpoints/notifications';
import { buildNotificationLink } from '@/utils/notificationLinks';
import { TYPE } from '@/theme/typography';

import { NOTIFICATION_COLORS } from '@/theme/colorTokens';

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  payroll_processed: { icon: <PayrollIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.payroll_processed },
  leave_approved: { icon: <LeaveIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.leave_approved },
  leave_rejected: { icon: <LeaveIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.leave_rejected },
  rent_reminder: { icon: <RentIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.rent_reminder },
  intercompany_confirmation: { icon: <IntercoIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.intercompany_confirmation },
  message: { icon: <MessageIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.message },
  welcome: { icon: <WelcomeIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.welcome },
  setup_reminder: { icon: <SetupIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.setup_reminder },
  holiday_reminder: { icon: <HolidayIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.holiday_reminder },
  industry_tip: { icon: <TipIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.industry_tip },
};

function getTypeConfig(type: string | null) {
  return TYPE_CONFIG[type || ''] || { icon: <DefaultIcon sx={{ fontSize: 16 }} />, color: NOTIFICATION_COLORS.default };
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString('en-GB', { dateStyle: 'medium' });
}

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications/unread-count'],
    queryFn: () => notificationsApi.unreadCount().then((res) => res.data.unread_count),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Fetch recent notifications when popover opens
  const { data, isLoading } = useQuery({
    queryKey: ['notifications/recent'],
    queryFn: () => notificationsApi.list({ per_page: 8 }).then((r) => r.data),
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/unread-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/unread-count'] });
    },
  });

  // Real-time push: when the backend fires a 'notification' event over WS,
  // invalidate so the badge bumps without waiting on the 30s poll.
  const { lastEvent } = useWebSocket({ enabled: true });
  useEffect(() => {
    if (lastEvent?.type === 'notification') {
      queryClient.invalidateQueries({ queryKey: ['notifications/unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  }, [lastEvent, queryClient]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const items: NotificationResponse[] = data?.items || [];
  const total = data?.total || 0;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton aria-label="Notifications" onClick={handleOpen} size="small">
          <Badge badgeContent={unreadCount} color="error">
            <NotifIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxHeight: 520,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Notifications</Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                color="error"
                sx={{ height: 20, fontSize: TYPE.xs, fontWeight: TYPE.bold }}
              />
            )}
          </Stack>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton
                size="small"
                aria-label="Mark all notifications as read"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                sx={{ color: 'primary.main' }}
              >
                <MarkAllIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Notification List */}
        <Box sx={{ overflowY: 'auto', maxHeight: 400 }}>
          {isLoading ? (
            <Stack spacing={0} sx={{ p: 0 }}>
              {[1, 2, 3, 4].map((i) => (
                <Box key={i} sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Skeleton variant="circular" width={32} height={32} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="80%" height={18} />
                      <Skeleton width="60%" height={14} sx={{ mt: 0.5 }} />
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : items.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <EmptyIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" sx={{ fontSize: TYPE.md }}>
                No notifications yet
              </Typography>
              <Typography color="text.disabled" sx={{ fontSize: TYPE.sm, mt: 0.25 }}>
                You're all caught up
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {items.map((n, idx) => {
                const config = getTypeConfig(n.type);
                return (
                  <ListItem
                    key={n.id}
                    divider={idx < items.length - 1}
                    sx={{
                      px: 2,
                      py: 1.25,
                      bgcolor: n.is_read ? 'transparent' : (theme) => alpha(theme.palette.primary.main, 0.04),
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      alignItems: 'flex-start',
                    }}
                    onClick={() => {
                      if (!n.is_read) markReadMutation.mutate(n.id);
                      const link = buildNotificationLink(n.type, n.reference_type, n.reference_id);
                      if (link) {
                        handleClose();
                        navigate(link);
                      }
                    }}
                    secondaryAction={
                      !n.is_read ? (
                        <Tooltip title="Mark as read">
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label="Mark notification as read"
                            onClick={(e) => {
                              e.stopPropagation();
                              markReadMutation.mutate(n.id);
                            }}
                            sx={{ mt: 0.5 }}
                          >
                            <ReadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 38, mt: 0.25 }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(config.color, 0.1),
                          color: config.color,
                        }}
                      >
                        {config.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {!n.is_read && <UnreadDot sx={{ fontSize: 7, color: 'primary.main', flexShrink: 0 }} />}
                          <Typography
                            sx={{
                              fontSize: TYPE.md,
                              fontWeight: n.is_read ? TYPE.normal : TYPE.semibold,
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {n.title}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Box>
                          {n.body && (
                            <Typography
                              sx={{
                                fontSize: TYPE.sm,
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                mt: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {n.body}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: TYPE.xs,
                              color: 'text.disabled',
                              mt: 0.5,
                            }}
                          >
                            {formatTime(n.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'background.default',
          }}
        >
          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
            {total > 8 ? `Showing 8 of ${total}` : `${items.length} notification${items.length !== 1 ? 's' : ''}`}
          </Typography>
          <Button
            size="small"
            endIcon={<ViewAllIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              handleClose();
              navigate('/notifications');
            }}
            sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, textTransform: 'none' }}
          >
            View All
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
